<template>
  <div class="document-viewer">
    <b-overlay :show="isFetching" rounded="sm" spinner-variant="primary">
      <!-- Compact Document Generation -->
      <div class="document-generation-compact">
        <DocumentTypeGrid
          :document-types="documentTypes"
          :generating-document="generatingDocument"
          :template-documents="templateDocuments"
          :signing-template="signingTemplate"
          @generate="handleDocumentGenerate"
          @template-sign="handleTemplateSign"
          @template-upload="handleTemplateUpload"
          @template-delete="handleTemplateDelete"
        />
      </div>

      <!-- Gallery Actions - Only show when files are selected -->
      <GalleryActions
        v-if="selectedFiles.length > 0"
        :selected-files="selectedFiles"
        @bulk-download="handleBulkDownload"
        @bulk-delete="handleBulkDelete"
      />

      <!-- Document Categories in compact layout -->
      <div class="document-categories">
        <CategoryCard
          v-for="category in documentCategories"
          :key="category.value"
          :title="category.text"
          :category="category.value"
          :items="filteredList(category.value)"
          :selected-files="selectedFiles"
          :patient-id="patient.id"
          @upload="showUploadModal"
          @upload-success="handleUploadSuccess"
          @upload-error="handleUploadError"
          @select="toggleFileSelection"
          @preview="handlePreview"
          @download="handleDownload"
          @delete="handleDelete"
          @sign="openSignTab"
          @radiiView="openRadiiView"
        />
      </div>
    </b-overlay>

    <!-- Hidden Document Generator -->
    <DocumentGenerator
      v-if="currentDocumentType !== 'medicalCert'"
      :key="currentDocumentType"
      ref="documentGenerator"
      :document-type="currentDocumentType"
      :modal-id="`${currentDocumentType}-modal`"
      :modal-ref="`${currentDocumentType}Modal`"
      modal-class="component-modal-treatment-planning"
      style="display: none"
    />

    <!-- Medical certificate edit-on-document modal -->
    <MedicalCertEditor
      v-if="currentDocumentType === 'medicalCert'"
      ref="medicalCertEditor"
      modal-id="medicalCert-modal"
      modal-ref="medicalCertModal"
    />

    <!-- Lightbox for document preview -->
    <Lightbox
      :index="lightBoxIndex"
      :items="lightBoxList"
      :toolbar="[
        'counter',
        'zoom',
        'slide',
        'rotate',
        'gallery',
        'fullscreen',
        'close',
      ]"
      @close="resetLightbox"
    />

    <!-- Modals -->
    <UploadFilesModal
      :patientId="patient.id"
      ref="uploadFiles"
      @uploadedFiles="handleDocumentUploads"
      @uploadedTemplateFiles="pollForUpdatesTemplate"
    />
    <Loading v-if="isLoading" />
    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import GalleryMixin from "@/mixins/GalleryMixin";
import ClinicFormPermissionMixin from "@/mixins/ClinicFormPermissionMixin";
import DocumentGenerator from "@/components/modal/DocumentGenerator/DocumentGenerator.vue";
import MedicalCertEditor from "@/components/modal/DocumentGenerator/MedicalCertEditor.vue";
import { documentTypes } from "@/components/modal/DocumentGenerator/config/documentConfig";
import { TEMPLATE_FETCH_PARAMS } from "@/constants";
import DocumentTypeGrid from "./DocumentTypeGrid.vue";
import CategoryCard from "../gallery/CategoryCard.vue";
import GalleryActions from "../gallery/GalleryActions.vue";
import UploadFilesModal from "@/components/modal/UploadFiles.vue";
import Dialog from "@/components/modal/Dialog.vue";
import Loading from "@/components/Loading.vue";
import VueLightbox from "vue-lightbox-lite";
import "vue-lightbox-lite/dist/index.css";
import { FEATURE } from "@/constants/entitlements";

export default {
  name: "DocumentViewer",
  mixins: [GalleryMixin, ClinicFormPermissionMixin],
  components: {
    DocumentGenerator,
    MedicalCertEditor,
    DocumentTypeGrid,
    CategoryCard,
    GalleryActions,
    UploadFilesModal,
    Dialog,
    Loading,
    Lightbox: VueLightbox,
  },
  props: {
    patient: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentDocumentType: "empty",
      generatingDocument: null,
      signingTemplate: null,
      documentCategories: [
        { text: "Document", value: "Document" },
        // { text: "Template Document", value: "Template" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      entitlementFlag: "moduleBranch/entitlementFlag",
    }),
    canGenerateDocument() {
      return this.entitlementFlag(FEATURE.DOCUMENT_GENERATOR);
    },
    documentTypes() {
      return documentTypes.map(item => {
        if (item.options) {
          return {
            ...item,
            options: item.options.map(option => ({
              ...option,
              disabled: option.checkForSubscription && !this.canGenerateDocument,
            })),
          };
        }
        return {
          ...item,
          disabled:
            item.disabled ||
            (item.checkForSubscription && !this.canGenerateDocument),
        };
      });
    },
    templateDocuments() {
      // Get template documents from the gallery mixin, filter to PDF only
      const templates = this.filteredList("Template") || [];
      return templates.filter(template => template.fileType === 'application/pdf');
    },
  },
  methods: {
    async showDocument() {
      try {
        await this.$nextTick();

        const ref =
          this.currentDocumentType === "medicalCert"
            ? this.$refs.medicalCertEditor
            : this.$refs.documentGenerator;

        if (!ref) {
          console.warn("Document generator reference not found");
          return;
        }

        ref.show(this.patient);
      } catch (error) {
        console.error("Error showing document:", error);
      }
    },

    handleDocumentUploads(e) {
      // For regular document uploads, use the standard polling method
      this.pollForUpdates(e);
    },

    async handleDocumentGenerate(documentType) {
      try {
        this.generatingDocument = documentType;
        this.currentDocumentType = documentType;

        await this.$nextTick();

        const ref =
          documentType === "medicalCert"
            ? this.$refs.medicalCertEditor
            : this.$refs.documentGenerator;

        if (!ref) {
          console.warn("Document generator reference not found");
          return;
        }

        ref.show(this.patient);
      } catch (error) {
        console.error("Error generating document:", error);
      } finally {
        // Reset generating state after a delay to show completion
        setTimeout(() => {
          this.generatingDocument = null;
        }, 1000);
      }
    },

    handleUploadSuccess({ category, count, files = [] }) {
      // Trigger polling to refresh file list
      if (category === "Template") {
        this.pollForUpdatesTemplate(files);
      } else {
        this.pollForUpdates(files);
      }
    },

    handleUploadError(error) {
      console.error("Upload error:", error);
      this.$bvToast.toast("เกิดข้อผิดพลาดในการอัปโหลดไฟล์", {
        title: "อัปโหลดล้มเหลว",
        variant: "danger",
        solid: true,
        autoHideDelay: 5000,
      });
    },

    async handleTemplateSign(templateId) {
      try {
        this.signingTemplate = templateId;

        // Find the template document
        const template = this.templateDocuments.find(t => t.id === templateId);
        if (!template) {
          console.error("Template not found:", templateId);
          return;
        }

        // Use the existing openSignTab method (from GalleryMixin)
        this.openSignTab(template);
      } catch (error) {
        console.error("Error opening template for signing:", error);
      } finally {
        // Reset signing state after a delay
        setTimeout(() => {
          this.signingTemplate = null;
        }, 1000);
      }
    },

    handleTemplateUpload() {
      // Show the upload modal specifically for template documents
      this.showUploadModal("Template");
    },

    // ลบแบบฟอร์มคลินิกที่ไม่ใช้แล้ว — soft delete ลงถังขยะ (กู้คืนได้) เหมือนไฟล์อื่น
    // ไม่ต้อง refetch: deleteMediaFile ตัด id ออกจาก state.fileTemplate ให้แล้วเมื่อ 200
    async handleTemplateDelete(templateId) {
      if (!this.canManageClinicForm) return;

      const template = this.templateDocuments.find(t => t.id === templateId);
      if (!template) {
        console.error("Template not found:", templateId);
        return;
      }

      const templateName = template.name || template.remark || "แบบฟอร์มนี้";
      try {
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "ลบแบบฟอร์มนี้ ?",
          `"${templateName}" จะถูกย้ายไปถังขยะ และหายจากรายการแบบฟอร์มของสาขานี้ (เอกสารที่คนไข้เซ็นไปแล้วยังอยู่ครบ)`
        );
        if (!isConfirmed) return;

        this.isLoading = true;
        const res = await this.deleteMediaFile({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          id: templateId,
        });
        if (res.status !== 200) {
          throw new Error(`Unexpected delete status ${res.status}`);
        }
      } catch (err) {
        console.error("Error deleting template:", err);
        this.$refs.Dialog.showAlert("ลบไม่สำเร็จ", "ไม่สามารถลบแบบฟอร์มนี้ได้");
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    // Fetch both regular files and template files for document mode
    this.fetchMediaFiles({
      clinicUrl: this.$route.params.clinicUrl,
      branchUrl: this.$route.params.branchUrl,
      params: { patientId: this.patient.id, limit: this.limit },
    });

    this.fecthMediaFilesTemplate({
      clinicUrl: this.$route.params.clinicUrl,
      branchUrl: this.$route.params.branchUrl,
      params: { ...TEMPLATE_FETCH_PARAMS },
    });
  },
};
</script>

<style scoped>
.document-viewer {
  padding: 0;
  max-width: 100%;
}

.document-generation-compact {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.compact-actions {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.document-categories {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.document-categories .category-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.document-categories .category-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .document-generation-compact {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .compact-actions {
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.75rem;
  }

  .document-categories {
    gap: 0.75rem;
  }
}

@media (max-width: 576px) {
  .document-generation-compact {
    padding: 0.625rem;
    margin-bottom: 0.75rem;
  }

  .compact-actions {
    padding: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .document-categories {
    gap: 0.5rem;
  }
}
</style>